import React from 'react';
import { ui } from '../../config/uiSizes';
import { useToast } from '../Toast/ToastProvider';
import { Tooltip } from '../Tooltip';
import { PreMigrationConfirmModal } from '../Modals/Migration/PreMigrationConfirmModal';
import { runRealImport } from '../../services/Migration/runRealImportService';
import { logger } from '../../utils/logger';

interface RunRealImportButtonProps {
  /** Bearer token for the API calls. */
  token: string;
  /** Migration whose previewed changes should be applied for real. */
  migrationId: string;
  /** Called after the real import is successfully queued (e.g. to close the modal). */
  onImportQueued?: (() => void) | undefined;
  /** Optional extra classes for the outer container. */
  className?: string;
}

/**
 * Converts a completed dry-run preview into a real import. Renders a primary action plus a
 * confirmation dialog (the real import DOES change the target org). On confirm it clears the dry-run
 * flag and re-enqueues the import on the same migration, reusing the already exported + transformed
 * artifacts.
 */
const RunRealImportButton: React.FC<RunRealImportButtonProps> = ({
  token,
  migrationId,
  onImportQueued,
  className = ''
}) => {
  const { showToast } = useToast();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await runRealImport(token, migrationId);
      setConfirmOpen(false);
      showToast('Import queued — this can take a few minutes.', 'success');
      onImportQueued?.();
    } catch (err) {
      logger.error('Run real import failed', {
        error: err instanceof Error ? err.message : String(err),
        migrationId
      });
      showToast('Failed to queue the import. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`inline-flex items-center ${ui.gap.xs} ${className}`}>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        className={`${ui.button.lg} bg-green-600 dark:bg-green-700 text-white font-semibold rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-all shadow-md hover:shadow-lg flex items-center justify-center ${ui.gap.sm}`}
      >
        <svg className={ui.svg.sm} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
            clipRule="evenodd"
          />
        </svg>
        <span>Run the real import</span>
      </button>
      <Tooltip
        position="top"
        content="Applies the previewed changes to the target org, reusing what you already exported and transformed."
      />

      <PreMigrationConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void handleConfirm()}
        confirmLoading={submitting}
      />
    </div>
  );
};

export default RunRealImportButton;
